import { useEffect, useState } from 'react';
import {
  makeStyles, tokens, Title1, Title3, Text, Textarea, Input, Button, Spinner,
  Badge, Divider, Dropdown, Option, Field,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { listRoles } from '../api/prompts';
import {
  listDatasets, getDataset, createDataset, updateDataset, deleteDataset,
  addQuestion, updateQuestion, deleteQuestion,
} from '../api/datasets';
import type {
  GoldenDatasetSummary, GoldenDatasetDetail, GoldenQuestionDetail, SignalCareRole,
} from '../api/types';

/**
 * Golden Datasets — curated question sets scoped to a SignalCare role.
 *
 * Datasets are the load-bearing input for M3's prompt-vs-provider matrix and
 * for any repeatable benchmark run. Every dataset belongs to one role; only
 * prompts of the same role can consume it (enforced at the M3 matrix
 * boundary, not here).
 *
 * Layout: Roles → Datasets → Detail (questions list + inline editor).
 */

const ROLE_LABEL: Record<SignalCareRole, string> = {
  MemberIntake: 'Member Intake',
  CareCoordinator: 'Care Coordinator',
  Lpn: 'LPN',
  Rn: 'RN',
  Doctor: 'Doctor',
  Billing: 'Billing',
  Claims: 'Claims',
};

const useStyles = makeStyles({
  page: { display: 'grid', gridTemplateColumns: '220px 320px 1fr', gap: '16px', height: 'calc(100vh - 96px)' },
  col: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  roleBtn: { justifyContent: 'flex-start', fontWeight: 500 },
  roleBtnActive: { background: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1 },
  dsRow: {
    padding: '10px 12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  dsRowActive: {
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
  meta: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' },
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  qRow: {
    padding: '12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  qNumBadge: {
    display: 'inline-block',
    minWidth: '24px',
    textAlign: 'center' as const,
    padding: '2px 6px',
    borderRadius: '4px',
    background: tokens.colorNeutralBackground3,
    fontSize: '11px',
    fontFamily: tokens.fontFamilyMonospace,
  },
  qActions: { display: 'flex', gap: '6px', marginLeft: 'auto' },
  keywords: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  err: { color: tokens.colorPaletteRedForeground1 },
});

export default function GoldenDatasets() {
  const s = useStyles();

  const [roles, setRoles] = useState<SignalCareRole[]>([]);
  const [activeRole, setActiveRole] = useState<SignalCareRole | 'ALL'>('ALL');
  const [datasets, setDatasets] = useState<GoldenDatasetSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<GoldenDatasetDetail | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showEditDs, setShowEditDs] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<GoldenQuestionDetail | null>(null);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try { setRoles(await listRoles()); }
      catch (e) { setErr((e as Error).message); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const items = await listDatasets(activeRole === 'ALL' ? undefined : activeRole);
        setDatasets(items);
        if (items.length && !items.some((d) => d.id === selectedId)) setSelectedId(items[0].id);
        else if (items.length === 0) { setSelectedId(null); setDetail(null); }
      } catch (e) { setErr((e as Error).message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRole]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    (async () => {
      try { setDetail(await getDataset(selectedId)); }
      catch (e) { setErr((e as Error).message); }
    })();
  }, [selectedId]);

  async function reload() {
    const items = await listDatasets(activeRole === 'ALL' ? undefined : activeRole);
    setDatasets(items);
    if (selectedId) setDetail(await getDataset(selectedId).catch(() => null));
  }

  async function onDeleteDs() {
    if (!detail) return;
    if (!confirm(`Delete "${detail.name}" and its ${detail.questions.length} question(s)?`)) return;
    try {
      await deleteDataset(detail.id);
      setSelectedId(null);
      await reload();
    } catch (e) { setErr((e as Error).message); }
  }

  async function onDeleteQ(q: GoldenQuestionDetail) {
    if (!detail) return;
    if (!confirm(`Delete question ${q.orderIndex}?`)) return;
    try {
      await deleteQuestion(detail.id, q.id);
      await reload();
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title1 as="h1">Golden Datasets</Title1>
        <Button appearance="primary" onClick={() => setShowCreate(true)}>New Dataset</Button>
      </div>
      {err && <div className={s.err} style={{ marginBottom: 12 }}>{err}</div>}

      <div className={s.page}>
        {/* Roles */}
        <div className={s.col}>
          <Text weight="semibold" size={200}>SignalCare Roles</Text>
          <Button
            appearance="subtle"
            className={`${s.roleBtn} ${activeRole === 'ALL' ? s.roleBtnActive : ''}`}
            onClick={() => setActiveRole('ALL')}
          >All roles</Button>
          {roles.map((r) => (
            <Button
              key={r}
              appearance="subtle"
              className={`${s.roleBtn} ${activeRole === r ? s.roleBtnActive : ''}`}
              onClick={() => setActiveRole(r)}
            >{ROLE_LABEL[r]}</Button>
          ))}
        </div>

        {/* Datasets */}
        <div className={s.col}>
          <Text weight="semibold" size={200}>Datasets ({datasets.length})</Text>
          {datasets.length === 0 && <Text size={200}>No datasets in this role yet.</Text>}
          {datasets.map((d) => (
            <div
              key={d.id}
              className={`${s.dsRow} ${selectedId === d.id ? s.dsRowActive : ''}`}
              onClick={() => setSelectedId(d.id)}
            >
              <Text weight="semibold">{d.name}</Text>
              <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{d.description}</Text>
              <div className={s.meta}>
                <Badge appearance="outline">{ROLE_LABEL[d.role]}</Badge>
                <Badge color="informative">{d.questionCount} question{d.questionCount === 1 ? '' : 's'}</Badge>
              </div>
            </div>
          ))}
        </div>

        {/* Detail */}
        <div className={s.col}>
          {!detail && <Text>Select a dataset to inspect.</Text>}
          {detail && (
            <>
              <div className={s.detailHeader}>
                <div>
                  <Title3 as="h2">{detail.name}</Title3>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{detail.description}</Text>
                  <div className={s.meta} style={{ marginTop: 6 }}>
                    <Badge appearance="outline">{ROLE_LABEL[detail.role]}</Badge>
                    <Badge color="informative">{detail.questions.length} questions</Badge>
                    <Text size={100}>Updated {new Date(detail.updatedAt).toLocaleString()}</Text>
                  </div>
                </div>
                <div className={s.actions}>
                  <Button size="small" onClick={() => setShowEditDs(true)}>Edit</Button>
                  <Button size="small" appearance="primary" onClick={() => setAddingQuestion(true)}>Add question</Button>
                  <Button size="small" appearance="subtle" onClick={onDeleteDs}>Delete</Button>
                </div>
              </div>

              <Divider style={{ margin: '12px 0' }} />

              {detail.questions.length === 0 && <Text size={200}>No questions yet. Click "Add question" to seed one.</Text>}
              {detail.questions.map((q) => (
                <div key={q.id} className={s.qRow}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={s.qNumBadge}>{q.orderIndex}</span>
                    <Text weight="semibold">{q.question}</Text>
                    <div className={s.qActions}>
                      <Button size="small" appearance="subtle" onClick={() => setEditingQuestion(q)}>Edit</Button>
                      <Button size="small" appearance="subtle" onClick={() => onDeleteQ(q)}>Delete</Button>
                    </div>
                  </div>
                  {q.notes && <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{q.notes}</Text>}
                  {q.expectedAnswerKeywords.length > 0 && (
                    <div className={s.keywords}>
                      {q.expectedAnswerKeywords.map((k) => (
                        <Badge key={k} appearance="outline" size="small">{k}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <CreateDatasetDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        roles={roles}
        defaultRole={activeRole === 'ALL' ? 'CareCoordinator' : activeRole}
        onCreated={async (created) => {
          setShowCreate(false);
          setActiveRole(created.role);
          setSelectedId(created.id);
          await reload();
        }}
      />
      <EditDatasetDialog
        open={showEditDs}
        onClose={() => setShowEditDs(false)}
        dataset={detail}
        onSaved={async () => { setShowEditDs(false); await reload(); }}
      />
      <QuestionDialog
        open={addingQuestion || editingQuestion !== null}
        onClose={() => { setAddingQuestion(false); setEditingQuestion(null); }}
        datasetId={detail?.id ?? null}
        question={editingQuestion}
        onSaved={async () => { setAddingQuestion(false); setEditingQuestion(null); await reload(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

function CreateDatasetDialog(props: {
  open: boolean;
  onClose: () => void;
  roles: SignalCareRole[];
  defaultRole: SignalCareRole;
  onCreated: (d: GoldenDatasetDetail) => void;
}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [role, setRole] = useState<SignalCareRole>(props.defaultRole);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { setRole(props.defaultRole); }, [props.defaultRole, props.open]);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const created = await createDataset({ name, description: desc, role });
      setName(''); setDesc('');
      props.onCreated(created);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={props.open} onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>New golden dataset</DialogTitle>
          <DialogContent>
            <div style={{ display: 'grid', gap: 12 }}>
              <Field label="Name" required>
                <Input value={name} onChange={(_, d) => setName(d.value)} />
              </Field>
              <Field label="Description">
                <Input value={desc} onChange={(_, d) => setDesc(d.value)} />
              </Field>
              <Field label="Role" required>
                <Dropdown
                  value={ROLE_LABEL[role]}
                  selectedOptions={[role]}
                  onOptionSelect={(_e, data) => data.optionValue && setRole(data.optionValue as SignalCareRole)}
                >
                  {props.roles.map((r) => (
                    <Option key={r} value={r}>{ROLE_LABEL[r]}</Option>
                  ))}
                </Dropdown>
              </Field>
              {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" onClick={save} disabled={busy || !name}>
              {busy ? <Spinner size="tiny" /> : 'Create'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function EditDatasetDialog(props: {
  open: boolean;
  onClose: () => void;
  dataset: GoldenDatasetDetail | null;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (props.open && props.dataset) {
      setName(props.dataset.name); setDesc(props.dataset.description); setErr(null);
    }
  }, [props.open, props.dataset]);

  async function save() {
    if (!props.dataset) return;
    setBusy(true); setErr(null);
    try {
      await updateDataset(props.dataset.id, { name, description: desc });
      props.onSaved();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={props.open} onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Edit dataset</DialogTitle>
          <DialogContent>
            <div style={{ display: 'grid', gap: 12 }}>
              <Field label="Name" required>
                <Input value={name} onChange={(_, d) => setName(d.value)} />
              </Field>
              <Field label="Description">
                <Input value={desc} onChange={(_, d) => setDesc(d.value)} />
              </Field>
              {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" onClick={save} disabled={busy || !name}>
              {busy ? <Spinner size="tiny" /> : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function QuestionDialog(props: {
  open: boolean;
  onClose: () => void;
  datasetId: string | null;
  question: GoldenQuestionDetail | null;
  onSaved: () => void;
}) {
  const [q, setQ] = useState('');
  const [notes, setNotes] = useState('');
  const [keywords, setKeywords] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isEdit = props.question !== null;

  useEffect(() => {
    if (props.open) {
      setQ(props.question?.question ?? '');
      setNotes(props.question?.notes ?? '');
      setKeywords((props.question?.expectedAnswerKeywords ?? []).join(', '));
      setErr(null);
    }
  }, [props.open, props.question]);

  async function save() {
    if (!props.datasetId) return;
    setBusy(true); setErr(null);
    const kws = keywords.split(',').map((k) => k.trim()).filter(Boolean);
    try {
      if (isEdit && props.question) {
        await updateQuestion(props.datasetId, props.question.id, {
          question: q, notes, expectedAnswerKeywords: kws, orderIndex: null,
        });
      } else {
        await addQuestion(props.datasetId, {
          question: q, notes, expectedAnswerKeywords: kws,
        });
      }
      props.onSaved();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={props.open} onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{isEdit ? 'Edit question' : 'Add question'}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'grid', gap: 12 }}>
              <Field label="Question" required>
                <Textarea rows={3} value={q} onChange={(_, d) => setQ(d.value)} />
              </Field>
              <Field label="Notes" hint="Authoring context — not shown to models.">
                <Textarea rows={2} value={notes} onChange={(_, d) => setNotes(d.value)} />
              </Field>
              <Field label="Expected answer keywords" hint="Comma-separated terms a good answer should mention.">
                <Input value={keywords} onChange={(_, d) => setKeywords(d.value)} />
              </Field>
              {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" onClick={save} disabled={busy || !q}>
              {busy ? <Spinner size="tiny" /> : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
